"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"
import {
  Search,
  Filter,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarIcon,
  Clock,
  Menu,
  BookOpen,
} from "lucide-react"
import { postAws } from "../postAws"
import { getClassData, getAllAssigmentData, getAllQuizData, getTeacherData } from "./api"

export default function Calendar() {
  const [loaded, setLoaded] = useState(false)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showCreateEventModal, setShowCreateEventModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredEvents, setFilteredEvents] = useState([])
  const [currentView, setCurrentView] = useState("dayGridMonth")
  const [showViewDropdown, setShowViewDropdown] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showSearch, setShowSearch] = useState(false)
  const [isCreatingEvent, setIsCreatingEvent] = useState(false)
  const [events, setEvents] = useState([])
  const [classRoomId, setClassRoomId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const calendarRef = useRef(null)
  const [newEvent, setNewEvent] = useState({
    title: "",
    start: "",
    startTime: "",
    end: "",
    endTime: "",
    description: "",
  })
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 0)

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Determine if we're on mobile
  const isMobile = windowWidth < 768

  // Helper function to generate recurring class events
  const generateRecurringClassEvents = (classData, startDate, endDate) => {
    const events = []
    const dayMap = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0,
    }

    if (!classData.schedule || classData.schedule.length === 0) {
      return events
    }

    classData.schedule.forEach((scheduleSlot) => {
      const dayOfWeek = dayMap[scheduleSlot.day.toLowerCase()]
      if (dayOfWeek === undefined) return

      // Start from the beginning of the current month or a reasonable past date
      const start = new Date(startDate)
      start.setDate(1) // Start from first day of month

      // Generate events for the next 6 months to cover a good range
      const end = new Date(endDate)
      end.setMonth(end.getMonth() + 6)

      // Find the first occurrence of the day in the range
      const currentDate = new Date(start)
      while (currentDate.getDay() !== dayOfWeek && currentDate <= end) {
        currentDate.setDate(currentDate.getDate() + 1)
      }

      // Generate weekly recurring events
      while (currentDate <= end) {
        const eventStart = new Date(currentDate)
        const eventEnd = new Date(currentDate)

        // Parse time strings (format: "HH:MM")
        const [startHour, startMinute] = scheduleSlot.startTime.split(":").map(Number)
        const [endHour, endMinute] = scheduleSlot.endTime.split(":").map(Number)

        eventStart.setHours(startHour, startMinute, 0, 0)
        eventEnd.setHours(endHour, endMinute, 0, 0)

        const now = new Date()
        const isPast = eventEnd < now

        events.push({
          id: `class-${classData.classRoomId}-${scheduleSlot.day}-${currentDate.getTime()}`,
          title: isMobile
            ? `${classData.classValue}${classData.section}`
            : `Class ${classData.classValue}${classData.section} - ${classData.subject}`,
          start: eventStart.toISOString(),
          end: eventEnd.toISOString(),
          backgroundColor: isPast ? "#9CA3AF" : "#3B82F6", // Grey for past, blue for upcoming
          borderColor: isPast ? "#6B7280" : "#2563EB",
          textColor: "white",
          extendedProps: {
            type: "class",
            classData: classData,
            scheduleSlot: scheduleSlot,
            isPast: isPast,
            originalTitle: `Class ${classData.classValue}${classData.section} - ${classData.subject}`,
            description: `${classData.subject} class for ${classData.classValue}${classData.section}`,
            classCode: classData.classCode,
            batchSize: classData.batchSize,
          },
        })

        // Move to next week
        currentDate.setDate(currentDate.getDate() + 7)
      }
    })

    return events
  }

  const fetchEvents = useCallback(async () => {
    if (!classRoomId) return

    try {
      setIsLoading(true)
      let allEvents = []

      // Fetch user-created events from getClassData
      const classDataResponse = await getClassData(classRoomId)
      if (
        classDataResponse &&
        classDataResponse.body &&
        classDataResponse.body.data &&
        classDataResponse.body.data.events
      ) {
        const userEvents = classDataResponse.body.data.events.map((event) => ({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            type: "event",
            originalTitle: event.title,
          },
        }))
        allEvents = [...allEvents, ...userEvents]
      }

      // Fetch teacher's classes with schedules
      const teacherId = localStorage.getItem("teacherId")
      if (teacherId) {
        try {
          const teacherDataResponse = await getTeacherData(teacherId)
          if (teacherDataResponse && teacherDataResponse.body && teacherDataResponse.body.data) {
            const createdClasses = teacherDataResponse.body.data.createdClasses || []

            // Generate recurring events for each class with schedule
            const now = new Date()
            const sixMonthsAgo = new Date()
            sixMonthsAgo.setMonth(now.getMonth() - 6)

            createdClasses.forEach((classData) => {
              if (classData.schedule && classData.schedule.length > 0) {
                const classEvents = generateRecurringClassEvents(classData, sixMonthsAgo, now)
                allEvents = [...allEvents, ...classEvents]
              }
            })
          }
        } catch (error) {
          console.error("Error fetching teacher data for classes:", error)
        }
      }

      // Fetch assignments
      const assignmentResponse = await getAllAssigmentData(classRoomId)
      console.log("Raw assignment response:", assignmentResponse)

      if (assignmentResponse) {
        let assignments = []

        if (typeof assignmentResponse === "string") {
          try {
            assignments = JSON.parse(assignmentResponse)
          } catch (e) {
            console.error("Error parsing assignment string response:", e)
          }
        } else if (Array.isArray(assignmentResponse)) {
          assignments = assignmentResponse
        } else if (assignmentResponse.body) {
          if (typeof assignmentResponse.body === "string") {
            try {
              assignments = JSON.parse(assignmentResponse.body)
            } catch (e) {
              console.error("Error parsing assignment body string:", e)
            }
          } else if (Array.isArray(assignmentResponse.body)) {
            assignments = assignmentResponse.body
          } else if (assignmentResponse.body && Array.isArray(assignmentResponse.body.body)) {
            assignments = assignmentResponse.body.body
          }
        }

        console.log("Extracted assignments array:", assignments)

        if (assignments && assignments.length > 0) {
          const assignmentEvents = assignments.map((assignment) => {
            console.log("Processing assignment:", assignment)
            const fullTitle = `Asgn: ${assignment.assignment_topic || assignment.assignmentTitle || "Untitled"}`

            return {
              id: assignment._id || `assignment-${Math.random().toString(36).substr(2, 9)}`,
              title: isMobile ? "Asgn" : fullTitle,
              fullTitle: fullTitle,
              start: assignment.assignmentDueDate,
              allDay: true,
              extendedProps: {
                description: assignment.assignmentTitle || assignment.assignment_topic || "No description",
                type: "assignment",
                shareWithStudents: assignment.shareWithStudents,
                status: assignment.assignmentStatus || "unknown",
                assignmentId: assignment._id,
                originalTitle: assignment.assignment_topic || assignment.assignmentTitle || "Untitled",
              },
            }
          })

          console.log("Created assignment events:", assignmentEvents)
          allEvents = [...allEvents, ...assignmentEvents]
        } else {
          console.warn("No assignments found or assignments array is empty")
        }
      }

      // Fetch quizzes
      const quizResponse = await getAllQuizData(classRoomId)
      if (quizResponse && quizResponse.body) {
        let quizzes = []
        if (typeof quizResponse.body === "string") {
          try {
            quizzes = JSON.parse(quizResponse.body)
          } catch (e) {
            console.error("Error parsing quiz data:", e)
          }
        } else if (Array.isArray(quizResponse.body)) {
          quizzes = quizResponse.body
        }

        const quizEvents = quizzes.map((quiz) => {
          const fullTitle = `Quiz: ${quiz.quiz_topic || quiz.quizTitle}`

          return {
            title: isMobile ? "Quiz" : fullTitle,
            fullTitle: fullTitle,
            start: quiz.quizDueDate,
            allDay: true,
            extendedProps: {
              description: quiz.quizTitle || quiz.quiz_topic,
              type: "quiz",
              shareWithStudents: quiz.shareWithStudents,
              status: quiz.quizStatus,
              originalTitle: quiz.quiz_topic || quiz.quizTitle,
            },
          }
        })
        allEvents = [...allEvents, ...quizEvents]
      }

      const formattedEvents = allEvents.map((event) => ({
        ...event,
        color: event.backgroundColor || getEventColor(event),
      }))

      console.log("All events:", formattedEvents)
      setEvents(formattedEvents)
      setFilteredEvents(formattedEvents)
    } catch (error) {
      console.error("Error fetching events:", error)
    } finally {
      setIsLoading(false)
      setLoaded(true)
    }
  }, [classRoomId, isMobile])

  useEffect(() => {
    const storedClassRoomId = localStorage.getItem("classRoomId")
    if (storedClassRoomId) {
      setClassRoomId(storedClassRoomId)
    }
  }, [])

  // Refetch events when mobile status changes to update titles
  useEffect(() => {
    if (classRoomId && loaded) {
      fetchEvents()
    }
  }, [classRoomId, fetchEvents, isMobile, loaded])

  // Initial fetch
  useEffect(() => {
    if (classRoomId) {
      fetchEvents()
    }
  }, [classRoomId, fetchEvents])

  // Auto-switch to list view on mobile
  useEffect(() => {
    if (isMobile && calendarRef.current) {
      if (currentView === "dayGridMonth") {
        // Keep month view but make it more compact
      } else if (currentView === "timeGridWeek" || currentView === "timeGridDay") {
        // These views are fine on mobile
      } else {
        // Default to list view for other cases on mobile
        setCurrentView("listMonth")
        calendarRef.current.getApi().changeView("listMonth")
      }
    }
  }, [isMobile, currentView])

  const getEventColor = (event) => {
    const now = new Date()
    const start = new Date(event.start)
    const end = new Date(event.end || event.start)

    // Past due dates are gray
    if (end < now) {
      return "#9CA3AF" // Grey for all past events
    }

    // Different colors for different event types
    if (event.extendedProps) {
      if (event.extendedProps.type === "assignment") {
        return "#FFA500" // Orange for assignments
      } else if (event.extendedProps.type === "quiz") {
        return "#8B5CF6" // Purple for quizzes
      } else if (event.extendedProps.type === "class") {
        return "#3B82F6" // Blue for classes
      } else if (event.extendedProps.type === "event") {
        return "#10B981" // Green for regular events
      }
    }

    // Default color for other events
    return "#10B981" // Green for upcoming events
  }

  const handleEventClick = (info) => {
    setSelectedEvent(info.event)
    setShowEventModal(true)
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    const filtered = events.filter((event) => {
      const searchTitle = event.fullTitle || event.title
      return searchTitle.toLowerCase().includes(e.target.value.toLowerCase())
    })
    setFilteredEvents(filtered)
  }

  const handleCreateEvent = async (e) => {
    e.preventDefault()

    // Validate that end date/time is not before start date/time
    const startDateTime = new Date(`${newEvent.start}T${newEvent.startTime}:00Z`)
    const endDateTime = new Date(`${newEvent.end}T${newEvent.endTime}:00Z`)

    if (endDateTime < startDateTime) {
      alert("End date and time cannot be earlier than start date and time")
      return
    }

    setIsCreatingEvent(true)
    try {
      if (!classRoomId) {
        throw new Error("ClassRoom ID not found")
      }
      const eventData = {
        title: newEvent.title,
        start: `${newEvent.start}T${newEvent.startTime}:00Z`,
        end: `${newEvent.end}T${newEvent.endTime}:00Z`,
        description: newEvent.description,
      }
      const payload = {
        request: "add-event",
        body: {
          classRoomId,
          eventData,
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/calender", "POST", payload)
      console.log("Event created:", response)
      setShowCreateEventModal(false)
      setNewEvent({
        title: "",
        start: "",
        startTime: "",
        end: "",
        endTime: "",
        description: "",
      })
      window.location.reload()
    } catch (error) {
      console.error("Error creating event:", error)
    } finally {
      setIsCreatingEvent(false)
    }
  }

  const handleViewChange = (view) => {
    setCurrentView(view)
    setShowViewDropdown(false)
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(view)
    }
  }

  const handleDateChange = (direction) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      if (direction === "prev") {
        calendarApi.prev()
      } else if (direction === "next") {
        calendarApi.next()
      } else if (direction === "today") {
        calendarApi.today()
      }
      setCurrentDate(calendarApi.getDate())
    }
  }

  // Custom event rendering for mobile
  const renderEventContent = (eventInfo) => {
    const eventType = eventInfo.event.extendedProps?.type || "event"
    const title = eventInfo.event.title
    const originalTitle = eventInfo.event.extendedProps?.originalTitle || title

    // For mobile display of user-entered events
    if (isMobile && eventType === "event") {
      const shortTitle = originalTitle.substring(0, 3).toUpperCase()

      return (
        <div
          className={`p-1 text-xs rounded cursor-pointer overflow-hidden`}
          style={{ backgroundColor: eventInfo.event.backgroundColor }}
        >
          <span className="font-semibold">{eventInfo.timeText}</span>
          <br />
          <span className="text-white font-medium">{shortTitle}</span>
        </div>
      )
    }

    // Special rendering for class events
    if (eventType === "class") {
      return (
        <div
          className={`p-1 text-xs rounded cursor-pointer overflow-hidden`}
          style={{ backgroundColor: eventInfo.event.backgroundColor }}
        >
          <div className="flex items-center">
            <BookOpen size={12} className="mr-1 text-white" />
            <span className="font-semibold text-white">{eventInfo.timeText}</span>
          </div>
          <span className="text-white font-medium">{title}</span>
        </div>
      )
    }

    // Regular display for desktop or assignment/quiz
    return (
      <div
        className={`p-1 text-xs rounded overflow-hidden`}
        style={{ backgroundColor: eventInfo.event.backgroundColor }}
      >
        <span className="font-semibold">{eventInfo.timeText}</span>
        <br />
        <span className="text-white font-medium">{title}</span>
      </div>
    )
  }

  if (!loaded) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 bg-gray-50 bg-gradient-to-br from-blue-50 to-indigo-100 -mt-6">
      <div className="shadow-lg rounded-lg overflow-hidden ">
        <div className="p-3 sm:p-6 border-b border-gray-200">
          {/* Mobile Header */}
          <div className="flex md:hidden justify-between items-center mb-4">
            <h1 className="text-xl font-bold text-gray-800">
              {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
            </h1>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 rounded-full hover:bg-gray-100 transition duration-300"
            >
              <Menu size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleDateChange("prev")}
                  className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition duration-300 flex items-center justify-center"
                >
                  <ChevronLeft size={20} className="text-gray-600 mr-1" />
                  <span>Prev</span>
                </button>
                <button
                  onClick={() => handleDateChange("next")}
                  className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition duration-300 flex items-center justify-center"
                >
                  <span>Next</span>
                  <ChevronRight size={20} className="text-gray-600 ml-1" />
                </button>
                <button
                  onClick={() => handleDateChange("today")}
                  className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
                >
                  Today
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowViewDropdown(!showViewDropdown)}
                    className="w-full p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition duration-300 flex items-center justify-center"
                  >
                    {currentView === "dayGridMonth"
                      ? "Month"
                      : currentView === "timeGridWeek"
                        ? "Week"
                        : currentView === "timeGridDay"
                          ? "Day"
                          : "List"}
                    <ChevronDown size={16} className="ml-1" />
                  </button>
                </div>
              </div>

              {showViewDropdown && (
                <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => handleViewChange("dayGridMonth")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Month
                  </button>
                  <button
                    onClick={() => handleViewChange("timeGridWeek")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Week
                  </button>
                  <button
                    onClick={() => handleViewChange("timeGridDay")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Day
                  </button>
                  <button
                    onClick={() => handleViewChange("listMonth")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    List
                  </button>
                </div>
              )}

              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="relative col-span-2">
                  <input
                    type="text"
                    placeholder="Search events..."
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <button className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition duration-300 flex items-center justify-center">
                  <Filter size={20} className="text-gray-600 mr-1" />
                  <span>Filter</span>
                </button>
                <button
                  onClick={() => setShowCreateEventModal(true)}
                  className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300 flex items-center justify-center"
                >
                  <Plus size={20} className="mr-1" />
                  <span>Add</span>
                </button>
              </div>
            </div>
          )}

          {/* Desktop Header */}
          <div className="hidden md:flex justify-between items-center -mt-6">
            <h1 className="text-3xl font-bold text-gray-800">
              {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
            </h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleDateChange("prev")}
                className="p-2 rounded-full hover:bg-gray-100 transition duration-300"
              >
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <button
                onClick={() => handleDateChange("next")}
                className="p-2 rounded-full hover:bg-gray-100 transition duration-300"
              >
                <ChevronRight size={24} className="text-gray-600" />
              </button>
              <button
                onClick={() => handleDateChange("today")}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
              >
                Today
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowViewDropdown(!showViewDropdown)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition duration-300 flex items-center"
                >
                  {currentView === "dayGridMonth"
                    ? "Month"
                    : currentView === "timeGridWeek"
                      ? "Week"
                      : currentView === "timeGridDay"
                        ? "Day"
                        : "List"}
                  <ChevronDown size={16} className="ml-2" />
                </button>
                {showViewDropdown && (
                  <div className="absolute mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <button
                      onClick={() => handleViewChange("dayGridMonth")}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Month
                    </button>
                    <button
                      onClick={() => handleViewChange("timeGridWeek")}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Week
                    </button>
                    <button
                      onClick={() => handleViewChange("timeGridDay")}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Day
                    </button>
                    <button
                      onClick={() => handleViewChange("listMonth")}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      List
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                {showSearch ? (
                  <input
                    type="text"
                    placeholder="Search events..."
                    className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onBlur={() => setShowSearch(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowSearch(true)}
                    className="p-2 rounded-full hover:bg-gray-100 transition duration-300"
                  >
                    <Search size={20} className="text-gray-600" />
                  </button>
                )}
                {showSearch && (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                )}
              </div>
              <button className="p-2 rounded-full hover:bg-gray-100 transition duration-300">
                <Filter size={20} className="text-gray-600" />
              </button>
              <button
                onClick={() => setShowCreateEventModal(true)}
                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-300"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>
        </div>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={currentView}
          headerToolbar={false}
          views={{
            dayGridMonth: { buttonText: "Month" },
            timeGridWeek: { buttonText: "Week" },
            timeGridDay: { buttonText: "Day" },
            listMonth: { buttonText: "List" },
          }}
          allDaySlot={false}
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          initialDate={currentDate}
          eventClick={handleEventClick}
          events={filteredEvents}
          height="auto"
          contentHeight="auto"
          aspectRatio={isMobile ? 0.8 : 1.35}
          eventContent={renderEventContent}
          datesSet={(dateInfo) => setCurrentDate(dateInfo.view.currentStart)}
          className="fc-theme-standard"
        />
      </div>

      {/* Updated Legend for event types - Responsive */}
      <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg shadow-sm">
        <h3 className="text-base sm:text-lg font-semibold mb-2">Calendar Legend</h3>
        <div className="flex flex-wrap gap-2 sm:gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#3B82F6] mr-1 sm:mr-2"></div>
            <span>Classes</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#FFA500] mr-1 sm:mr-2"></div>
            <span>Assignments</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#8B5CF6] mr-1 sm:mr-2"></div>
            <span>Quizzes</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#10B981] mr-1 sm:mr-2"></div>
            <span>Events</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#9CA3AF] mr-1 sm:mr-2"></div>
            <span>Past Due</span>
          </div>
        </div>
      </div>

      {/* Enhanced Event Modal - Responsive */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words pr-2">
                {selectedEvent.extendedProps?.type === "class" && "📚 "}
                {selectedEvent.extendedProps?.type === "assignment" && "📝 Assignment: "}
                {selectedEvent.extendedProps?.type === "quiz" && "📊 Quiz: "}
                {selectedEvent.extendedProps?.type === "event" && "📅 "}
                {selectedEvent.extendedProps?.originalTitle || selectedEvent.title}
              </h2>
              <button
                onClick={() => setShowEventModal(false)}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center text-gray-600">
                <CalendarIcon size={18} className="mr-2 flex-shrink-0" />
                <p className="text-sm sm:text-base">
                  {selectedEvent.extendedProps &&
                  (selectedEvent.extendedProps.type === "assignment" || selectedEvent.extendedProps.type === "quiz")
                    ? "Due Date: "
                    : selectedEvent.extendedProps?.type === "class"
                      ? "Class Date: "
                      : "Date: "}
                  {new Date(selectedEvent.start).toLocaleDateString()}
                  {selectedEvent.end && selectedEvent.end !== selectedEvent.start && (
                    <> - {new Date(selectedEvent.end).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              {!selectedEvent.allDay && (
                <div className="flex items-center text-gray-600">
                  <Clock size={18} className="mr-2 flex-shrink-0" />
                  <p className="text-sm sm:text-base">
                    {new Date(selectedEvent.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {selectedEvent.end && (
                      <>
                        {" "}
                        - {new Date(selectedEvent.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Special display for class events */}
              {selectedEvent.extendedProps?.type === "class" && (
                <div className="bg-blue-50 p-3 sm:p-4 rounded-md">
                  <h3 className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">Class Details:</h3>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p>
                      <strong>Class Code:</strong> {selectedEvent.extendedProps.classCode}
                    </p>
                    <p>
                      <strong>Batch Size:</strong> {selectedEvent.extendedProps.batchSize}
                    </p>
                    <p>
                      <strong>Status:</strong> {selectedEvent.extendedProps.isPast ? "Completed" : "Upcoming"}
                    </p>
                  </div>
                </div>
              )}

              {selectedEvent.extendedProps &&
                selectedEvent.extendedProps.type &&
                selectedEvent.extendedProps.type !== "class" && (
                  <div className="text-gray-600 text-sm sm:text-base">
                    <p>
                      <strong>Type:</strong>{" "}
                      {selectedEvent.extendedProps.type.charAt(0).toUpperCase() +
                        selectedEvent.extendedProps.type.slice(1)}
                    </p>
                    {selectedEvent.extendedProps.shareWithStudents !== undefined && (
                      <p>
                        <strong>Shared with Students:</strong>{" "}
                        {selectedEvent.extendedProps.shareWithStudents ? "Yes" : "No"}
                      </p>
                    )}
                    {selectedEvent.extendedProps.status && (
                      <p>
                        <strong>Status:</strong> {selectedEvent.extendedProps.status}
                      </p>
                    )}
                  </div>
                )}

              <div className="bg-gray-100 p-3 sm:p-4 rounded-md">
                <h3 className="font-semibold text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">Description:</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  {selectedEvent.extendedProps?.description || "No description provided for this event."}
                </p>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 flex justify-end">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300 text-sm sm:text-base mr-2"
              >
                Close
              </button>
              {selectedEvent.extendedProps?.type !== "class" && (
                <button className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300 text-sm sm:text-base">
                  Edit Event
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal - Responsive */}
      {showCreateEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-2xl font-bold">Create New Event</h2>
              <button onClick={() => setShowCreateEventModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateEvent}>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">Event Name</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={newEvent.start}
                  onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="time"
                  value={newEvent.startTime}
                  onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={newEvent.end}
                  onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  value={newEvent.endTime}
                  onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  rows="3"
                ></textarea>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-300 flex items-center justify-center text-sm sm:text-base"
                  disabled={isCreatingEvent}
                >
                  {isCreatingEvent ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    "Create Event"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style jsx global>{`
        .fc-theme-standard {
          font-family: 'Inter', sans-serif;
        }
        .fc-header-toolbar {
          padding: 1rem;
          background-color: #f3f4f6;
        }
        .fc-toolbar-title {
          font-size: 1.5rem !important;
          font-weight: 600;
        }
        .fc-button-primary {
          background-color: #3B82F6 !important;
          border-color: #3B82F6 !important;
        }
        .fc-button-primary:hover {
          background-color: #2563EB !important;
          border-color: #2563EB !important;
        }
        .fc-day-today {
          background-color: #EFF6FF !important;
        }
        .fc-event {
          border: none !important;
          padding: 2px 4px;
          border-radius: 4px;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .fc-toolbar-title {
            font-size: 1.2rem !important;
          }
          .fc-col-header-cell-cushion {
            padding: 4px !important;
          }
          .fc-daygrid-day-number {
            padding: 2px 4px !important;
            font-size: 0.875rem;
          }
          .fc-event {
            padding: 1px 2px;
            font-size: 0.75rem;
          }
          .fc-list-day-cushion {
            padding: 6px 8px !important;
          }
          .fc-list-event-time {
            width: 80px !important;
            font-size: 0.75rem !important;
          }
          .fc-list-event-title {
            font-size: 0.875rem !important;
          }
        }
      `}</style>
    </div>
  )
}
